/**
 * MapEditorScene — visual map editor for creating and editing map layouts.
 *
 * Features:
 * - Tile grid painting (Ground, Wall, Path, Placement, Base, Spawn)
 * - Grid resize (cols × rows)
 * - Path waypoint editor (click tiles in sequence to build paths)
 * - Decoration painting (continuous drag)
 * - Undo / Redo (Ctrl+Z / Ctrl+Shift+Z)
 * - Map properties (name, difficulty, allowed enemies)
 * - Export map as JSON (copy to clipboard)
 * - Load map from JSON
 * - Procedural generation with complexity levels
 *
 * Sub-modules:
 * - EditorTypes.ts — constants, enums, layout, texture maps
 * - EditorToolbar.ts — left toolbar UI (mode sections, palette, buttons)
 * - PathEditor.ts — path/waypoint editing logic
 * - MapIO.ts — export/import/save/procedural generation
 */
import Phaser from 'phaser';
import { TileType, CANVAS_WIDTH, CANVAS_HEIGHT, EnemyType, EnemyGrade, DecorType, DecorDef } from '@/config/GameConfig';
import { MapDifficulty, MapDefinition, CinematicTrigger } from '@/map/MapDefs';
import { CustomMapStore } from '@/systems/CustomMapStore';
import { SoundManager } from '@/systems/SoundManager';
import { PropsPanel } from '@/scenes/editor/PropsPanel';

// Sub-modules
import {
  EditorMode, EditorSnapshot,
  TOOLBAR_W, PROPS_X, PROPS_W,
  GRID_AREA_X, GRID_AREA_W, GRID_AREA_Y, GRID_AREA_H,
  MAX_UNDO, COMPLEXITY_LABELS, RICHNESS_LABELS, HAZARD_LABELS, INTENSITY_LABELS, DIFFICULTIES,
  ALL_ENEMY_TYPES,
  TILE_TEXTURES, DECOR_TEXTURES,
} from './mapeditor/EditorTypes';
import { buildToolbar, updateModeButtons, ToolbarRefs } from './mapeditor/EditorToolbar';
import * as PathEd from './mapeditor/PathEditor';
import * as MapIO from './mapeditor/MapIO';

export class MapEditorScene extends Phaser.Scene {
  // Grid state
  private gridCols = 20;
  private gridRows = 15;
  private grid: TileType[][] = [];

  // Path editing
  private paths: { col: number; row: number }[][] = [];
  private currentPath: { col: number; row: number }[] = [];
  private editingPathIndex = -1;

  // Map properties
  private mapName = 'New Map';
  private mapId = '';
  private mapDifficulty: MapDifficulty = 'Easy';
  private mapWaveCount = 10;
  private campaignLevelId: string | null = null;
  private hasOverride = false;
  private allowedEnemies: Set<EnemyType> = new Set([EnemyType.GRUNT, EnemyType.RUNNER]);
  private allowedGrades: Set<EnemyGrade> = new Set();
  private cinematicTriggers: CinematicTrigger[] = [];

  // Decoration state
  private decorations: DecorDef[] = [];
  private selectedDecor: DecorType = DecorType.TREE;
  private decorBtns: Map<DecorType, Phaser.GameObjects.Rectangle> = new Map();

  // UI state
  private selectedTile: TileType = TileType.PATH;
  private mode: EditorMode = EditorMode.PAINT;
  private isPainting = false;

  // Procedural generation
  private complexity: number = 1;
  private richness: number = 2; // 0=Barren, 1=Poor, 2=Normal, 3=Rich, 4=Abundant
  private hazards: number = 2;  // 0=None, 1=Low, 2=Normal, 3=High, 4=Extreme
  private intensity: number = 2; // 0=Trivial, 1=Easy, 2=Normal, 3=Hard, 4=Brutal

  // Props panel (tabbed right panel)
  private propsPanel!: PropsPanel;

  // Undo / Redo
  private undoStack: EditorSnapshot[] = [];
  private redoStack: EditorSnapshot[] = [];
  private undoText!: Phaser.GameObjects.Text;

  // Phaser objects
  private gridContainer!: Phaser.GameObjects.Container;
  private tileRects: Phaser.GameObjects.Rectangle[][] = [];
  private pathGfx!: Phaser.GameObjects.Graphics;
  private tileSize = 30;
  private baseTileSize = 30;
  private gridOffsetX = 0;
  private gridOffsetY = 0;

  // Zoom & pan
  private zoomLevel = 1;
  private panX = 0;
  private panY = 0;
  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private zoomText!: Phaser.GameObjects.Text;
  private fpsText!: Phaser.GameObjects.Text;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasdKeys!: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };

  // Tile pick mode (for cinematic trigger tile selection)
  private tilePickCallback: ((col: number, row: number) => void) | null = null;

  // Toolbar UI refs
  private toolBtns: Map<TileType, Phaser.GameObjects.Rectangle> = new Map();
  private modeBtns: Map<EditorMode, Phaser.GameObjects.Container> = new Map();
  private statusText!: Phaser.GameObjects.Text;
  private sizeText!: Phaser.GameObjects.Text;
  private resourceBtns: Map<DecorType, Phaser.GameObjects.Rectangle> = new Map();

  // Mode-dependent UI containers
  private paintSection!: Phaser.GameObjects.Container;
  private pathSection!: Phaser.GameObjects.Container;
  private decorSection!: Phaser.GameObjects.Container;
  private resourceSection!: Phaser.GameObjects.Container;
  private eraseSection!: Phaser.GameObjects.Container;
  private pathListText!: Phaser.GameObjects.Text;

  // Palette scroll state
  private paletteScrollY = 0;
  private paletteVisibleH = 0;

  constructor() {
    super({ key: 'MapEditorScene' });
  }

  private returnTo = 'TitleScene';

  create(data?: { mapId?: string; mapDef?: MapDefinition; campaignLevelId?: string; hasOverride?: boolean; returnTo?: string }): void {
    this.returnTo = data?.returnTo ?? (data?.campaignLevelId ? 'LevelTreeScene' : 'TitleScene');
    this.cameras.main.setBackgroundColor('#0a0a18');

    // Reset undo/redo and zoom/pan
    this.undoStack = [];
    this.redoStack = [];
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;

    // Campaign override context
    this.campaignLevelId = data?.campaignLevelId ?? null;
    this.hasOverride = data?.hasOverride ?? false;

    // Initialize grid (or load existing map for editing)
    if (data?.mapDef) {
      const m = data.mapDef;
      this.gridCols = m.cols;
      this.gridRows = m.rows;
      this.grid = m.grid.map(row => [...row]);
      this.paths = m.paths.map(p => p.map(wp => ({ col: wp.col, row: wp.row })));
      this.mapName = m.name || 'Campaign Map';
      this.mapId = m.id || `campaign_${Date.now()}`;
      this.mapDifficulty = (m.difficulty as MapDifficulty) || 'Easy';
      this.mapWaveCount = Phaser.Math.Clamp(m.waveCount ?? 10, 1, 100);
      this.allowedEnemies = new Set(m.allowedEnemies || []);
      this.allowedGrades = new Set(m.allowedGrades || []);
      this.intensity = m.intensity ?? 2;
      this.decorations = [...(m.decorations ?? [])];
      this.cinematicTriggers = [...(m.cinematics ?? [])];
    } else if (data?.mapId) {
      const existing = CustomMapStore.getById(data.mapId);
      if (existing) {
        this.gridCols = existing.cols;
        this.gridRows = existing.rows;
        this.grid = existing.grid.map(row => [...row]);
        this.paths = existing.paths.map(p => p.map(wp => ({ col: wp.col, row: wp.row })));
        this.mapName = existing.name;
        this.mapId = existing.id;
        this.mapDifficulty = existing.difficulty;
        this.mapWaveCount = Phaser.Math.Clamp(existing.waveCount ?? 10, 1, 100);
        this.allowedEnemies = new Set(existing.allowedEnemies);
        this.allowedGrades = new Set(existing.allowedGrades || []);
        this.intensity = existing.intensity ?? 2;
        this.decorations = [...(existing.decorations ?? [])];
        this.cinematicTriggers = [...(existing.cinematics ?? [])];
      } else {
        this.resetMapProperties();
        this.initGrid();
      }
    } else {
      this.resetMapProperties();
      this.initGrid();
    }

    // Grid container (rendered first, below everything)
    this.gridContainer = this.add.container(0, 0).setDepth(0);
    this.pathGfx = this.add.graphics().setDepth(1);

    // Build UI panels
    this.buildToolbarUI();
    this.buildPropsPanel();
    this.buildTopBar();

    // Opaque clip overlays — hide grid tiles that overflow into panel zones
    const bgColor = 0x0a0a18;
    this.add.rectangle(TOOLBAR_W / 2, CANVAS_HEIGHT / 2, TOOLBAR_W, CANVAS_HEIGHT, bgColor)
      .setDepth(6);
    this.add.rectangle(PROPS_X + PROPS_W / 2, CANVAS_HEIGHT / 2, PROPS_W, CANVAS_HEIGHT, bgColor)
      .setDepth(6);
    this.add.rectangle(CANVAS_WIDTH / 2, GRID_AREA_Y / 2, CANVAS_WIDTH, GRID_AREA_Y, bgColor)
      .setDepth(6);

    // Elevate all UI elements above grid clip overlays (depth 6)
    for (const obj of this.children.list) {
      if (obj === this.gridContainer || obj === this.pathGfx) continue;
      const go = obj as unknown as { depth?: number; setDepth?: (d: number) => void };
      if (typeof go.setDepth === 'function' && (go.depth ?? 0) < 6) {
        go.setDepth(10);
      }
    }

    // Render grid tiles
    this.renderGrid();

    // Save initial state
    this.pushUndo();

    // Wire toolbar path events to PathEditor functions
    this.events.on('toolbar:finishPath', () => this.finishCurrentPath());
    this.events.on('toolbar:deleteLastPath', () => this.deleteLastPath());
    this.events.on('toolbar:autoGeneratePaths', () => this.autoGeneratePaths());

    // Global input: painting with drag (left-click only)
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.tilePickCallback && p.leftButtonDown() && this.isInGridArea(p.x, p.y)) {
        const col = Math.floor((p.x - this.gridOffsetX) / this.tileSize);
        const row = Math.floor((p.y - this.gridOffsetY) / this.tileSize);
        if (col >= 0 && col < this.gridCols && row >= 0 && row < this.gridRows) {
          const cb = this.tilePickCallback;
          this.tilePickCallback = null;
          cb(col, row);
        }
        return;
      }
      if (p.middleButtonDown() || p.rightButtonDown()) {
        if (this.isInGridArea(p.x, p.y)) {
          this.isPanning = true;
          this.panStartX = p.x;
          this.panStartY = p.y;
        }
        return;
      }
      if (this.isInGridArea(p.x, p.y)) {
        this.isPainting = true;
        if (this.mode === EditorMode.PAINT || this.mode === EditorMode.DECOR || this.mode === EditorMode.RESOURCE || this.mode === EditorMode.ERASE) {
          this.pushUndo();
        }
        this.handleGridClick(p.x, p.y);
      }
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (this.isPanning && p.isDown) {
        const dx = p.x - this.panStartX;
        const dy = p.y - this.panStartY;
        this.panX += dx;
        this.panY += dy;
        this.panStartX = p.x;
        this.panStartY = p.y;
        this.renderGrid();
        return;
      }
      if (this.isPainting && p.isDown) {
        if (this.mode === EditorMode.PAINT || this.mode === EditorMode.DECOR || this.mode === EditorMode.RESOURCE || this.mode === EditorMode.ERASE) {
          this.handleGridClick(p.x, p.y);
        }
      }
    });
    this.input.on('pointerup', () => {
      this.isPainting = false;
      this.isPanning = false;
    });

    // Mouse wheel zoom / palette scroll / props panel scroll
    this.input.on('wheel', (p: Phaser.Input.Pointer, _gos: unknown[], _dx: number, dy: number) => {
      if (p.x < TOOLBAR_W) {
        this.scrollPalette(dy);
        return;
      }
      if (p.x >= PROPS_X) {
        this.propsPanel.scrollActiveTab(dy);
        return;
      }

      const oldZoom = this.zoomLevel;
      const step = 0.01 * Math.sign(dy);
      const newZoom = Phaser.Math.Clamp(oldZoom - step, 0.3, 4);
      if (newZoom === oldZoom) return;

      const oldTs = this.baseTileSize * oldZoom;
      const totalWOld = this.gridCols * oldTs;
      const totalHOld = this.gridRows * oldTs;
      const cxOld = GRID_AREA_X + (GRID_AREA_W - totalWOld) / 2;
      const cyOld = GRID_AREA_Y + (GRID_AREA_H - totalHOld) / 2;
      const worldX = p.x - cxOld - this.panX;
      const worldY = p.y - cyOld - this.panY;

      const newTs = this.baseTileSize * newZoom;
      const totalWNew = this.gridCols * newTs;
      const totalHNew = this.gridRows * newTs;
      const cxNew = GRID_AREA_X + (GRID_AREA_W - totalWNew) / 2;
      const cyNew = GRID_AREA_Y + (GRID_AREA_H - totalHNew) / 2;

      const scale = newZoom / oldZoom;
      const newWorldX = worldX * scale;
      const newWorldY = worldY * scale;

      this.panX = p.x - cxNew - newWorldX;
      this.panY = p.y - cyNew - newWorldY;
      this.zoomLevel = newZoom;
      this.renderGrid();
      this.updateZoomText();
    });

    // Disable right-click context menu on canvas
    this.game.canvas.oncontextmenu = (e: Event) => { e.preventDefault(); };

    // Cursor keys + WASD for panning
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasdKeys = {
      W: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      A: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      S: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };

    // Keyboard shortcuts
    const exitTarget = this.returnTo;
    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.tilePickCallback) {
        this.tilePickCallback = null;
        this.setStatus('Tile pick cancelled.');
        return;
      }
      if (this.mode === EditorMode.PATH && this.currentPath.length > 0) {
        this.finishCurrentPath();
      } else {
        SoundManager.play('click');
        this.scene.start(exitTarget);
      }
    });
    this.input.keyboard!.on('keydown-Z', (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.shiftKey) {
          this.redo();
        } else {
          this.undo();
        }
      }
    });
    this.input.keyboard!.on('keydown-ZERO', () => {
      this.zoomLevel = 1;
      this.panX = 0;
      this.panY = 0;
      this.renderGrid();
      this.updateZoomText();
    });
  }

  private isInGridArea(x: number, y: number): boolean {
    return x >= GRID_AREA_X && x <= GRID_AREA_X + GRID_AREA_W &&
           y >= GRID_AREA_Y && y <= GRID_AREA_Y + GRID_AREA_H;
  }

  // ══════════════════════════════════════════
  //  UNDO / REDO
  // ══════════════════════════════════════════

  private takeSnapshot(): EditorSnapshot {
    return {
      grid: this.grid.map(row => [...row]),
      decorations: this.decorations.map(d => ({ ...d })),
      paths: this.paths.map(p => p.map(wp => ({ ...wp }))),
      cols: this.gridCols,
      rows: this.gridRows,
    };
  }

  private applySnapshot(snap: EditorSnapshot): void {
    this.gridCols = snap.cols;
    this.gridRows = snap.rows;
    this.grid = snap.grid.map(row => [...row]);
    this.decorations = snap.decorations.map(d => ({ ...d }));
    this.paths = snap.paths.map(p => p.map(wp => ({ ...wp })));
    this.currentPath = [];
    this.updateSizeText();
    this.renderGrid();
  }

  pushUndo(): void {
    this.undoStack.push(this.takeSnapshot());
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
    this.redoStack = [];
    this.updateUndoText();
  }

  undo(): void {
    if (this.undoStack.length <= 1) {
      this.setStatus('Nothing to undo.');
      return;
    }
    this.redoStack.push(this.undoStack.pop()!);
    const snap = this.undoStack[this.undoStack.length - 1];
    this.applySnapshot(snap);
    this.updateUndoText();
    this.setStatus('Undo.');
  }

  redo(): void {
    if (this.redoStack.length === 0) {
      this.setStatus('Nothing to redo.');
      return;
    }
    const snap = this.redoStack.pop()!;
    this.undoStack.push(snap);
    this.applySnapshot(snap);
    this.updateUndoText();
    this.setStatus('Redo.');
  }

  private updateUndoText(): void {
    if (this.undoText) {
      this.undoText.setText(`↩${this.undoStack.length - 1} ↪${this.redoStack.length}`);
    }
  }

  // ══════════════════════════════════════════
  //  GRID MANAGEMENT
  // ══════════════════════════════════════════

  private resetMapProperties(): void {
    this.mapId = '';
    this.mapName = 'New Map';
    this.mapDifficulty = 'Easy';
    this.mapWaveCount = 10;
    this.paths = [];
    this.currentPath = [];
    this.editingPathIndex = -1;
    this.allowedEnemies = new Set();
    this.allowedGrades = new Set();
    this.decorations = [];
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;
  }

  private initGrid(): void {
    this.grid = [];
    for (let r = 0; r < this.gridRows; r++) {
      this.grid[r] = [];
      for (let c = 0; c < this.gridCols; c++) {
        this.grid[r][c] = TileType.GROUND;
      }
    }
  }

  resizeGrid(newCols: number, newRows: number): void {
    this.pushUndo();
    const oldGrid = this.grid;
    const oldRows = this.gridRows;
    const oldCols = this.gridCols;
    this.gridCols = Math.max(8, Math.min(40, newCols));
    this.gridRows = Math.max(6, Math.min(30, newRows));
    this.grid = [];
    for (let r = 0; r < this.gridRows; r++) {
      this.grid[r] = [];
      for (let c = 0; c < this.gridCols; c++) {
        this.grid[r][c] = (r < oldRows && c < oldCols) ? oldGrid[r][c] : TileType.GROUND;
      }
    }
    this.paths = this.paths.filter(p =>
      p.every(wp => wp.col >= 0 && wp.col < this.gridCols && wp.row >= 0 && wp.row < this.gridRows)
    );
    this.renderGrid();
    this.updateSizeText();
  }

  update(): void {
    this.fpsText.setText(`${Math.round(this.game.loop.actualFps)} FPS`);

    const panSpeed = 5;
    let dx = 0;
    let dy = 0;
    if (this.cursors.left.isDown || this.wasdKeys.A.isDown) dx += panSpeed;
    if (this.cursors.right.isDown || this.wasdKeys.D.isDown) dx -= panSpeed;
    if (this.cursors.up.isDown || this.wasdKeys.W.isDown) dy += panSpeed;
    if (this.cursors.down.isDown || this.wasdKeys.S.isDown) dy -= panSpeed;
    if (dx !== 0 || dy !== 0) {
      this.panX += dx;
      this.panY += dy;
      this.renderGrid();
    }
  }

  private computeTileSize(): void {
    const maxTileW = Math.floor(GRID_AREA_W / this.gridCols);
    const maxTileH = Math.floor(GRID_AREA_H / this.gridRows);
    this.baseTileSize = Math.max(12, Math.min(50, Math.min(maxTileW, maxTileH)));
    this.tileSize = this.baseTileSize * this.zoomLevel;
    const totalW = this.gridCols * this.tileSize;
    const totalH = this.gridRows * this.tileSize;
    const centerX = GRID_AREA_X + (GRID_AREA_W - totalW) / 2;
    const centerY = GRID_AREA_Y + (GRID_AREA_H - totalH) / 2;
    this.clampPan(totalW, totalH);
    this.gridOffsetX = centerX + this.panX;
    this.gridOffsetY = centerY + this.panY;
  }

  private clampPan(totalW: number, totalH: number): void {
    if (totalW <= GRID_AREA_W) {
      this.panX = 0;
    } else {
      const maxPan = (totalW - GRID_AREA_W) / 2 + 20;
      this.panX = Phaser.Math.Clamp(this.panX, -maxPan, maxPan);
    }
    if (totalH <= GRID_AREA_H) {
      this.panY = 0;
    } else {
      const maxPan = (totalH - GRID_AREA_H) / 2 + 20;
      this.panY = Phaser.Math.Clamp(this.panY, -maxPan, maxPan);
    }
  }

  // ══════════════════════════════════════════
  //  RENDERING
  // ══════════════════════════════════════════

  renderGrid(): void {
    this.gridContainer.removeAll(true);
    this.tileRects = [];
    this.pathGfx.clear();
    this.computeTileSize();
    const ts = this.tileSize;

    for (let r = 0; r < this.gridRows; r++) {
      this.tileRects[r] = [];
      for (let c = 0; c < this.gridCols; c++) {
        const tile = this.grid[r][c];
        const x = this.gridOffsetX + c * ts + ts / 2;
        const y = this.gridOffsetY + r * ts + ts / 2;

        const texKey = TILE_TEXTURES[tile];
        const img = this.add.image(x, y, texKey);
        img.setDisplaySize(ts - 1, ts - 1);
        img.setDepth(1);
        this.gridContainer.add(img);

        const rect = this.add.rectangle(x, y, ts - 1, ts - 1, 0x000000, 0).setDepth(1.5);
        this.gridContainer.add(rect);
        this.tileRects[r][c] = rect;

        if (tile === TileType.PLACEMENT) {
          const border = this.add.rectangle(x, y, ts - 1, ts - 1);
          border.setStrokeStyle(1, 0x44aa44, 0.3);
          border.setFillStyle(0x000000, 0);
          border.setDepth(1.6);
          this.gridContainer.add(border);
        } else if (tile === TileType.BROKEN_PLACEMENT) {
          const border = this.add.rectangle(x, y, ts - 1, ts - 1);
          border.setStrokeStyle(1, 0xaa6644, 0.3);
          border.setFillStyle(0x000000, 0);
          border.setDepth(1.6);
          this.gridContainer.add(border);
        }

        if (tile === TileType.BASE || tile === TileType.SPAWN) {
          const label = tile === TileType.BASE ? 'B' : 'S';
          const txt = this.add.text(x, y, label, {
            fontSize: `${Math.max(8, ts * 0.4)}px`, color: '#ffffff',
            fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(2);
          this.gridContainer.add(txt);
        }
      }
    }

    this.drawDecorations();
    this.drawPathsAndUpdateList();
  }

  private drawDecorations(): void {
    const ts = this.tileSize;
    for (const decor of this.decorations) {
      const x = this.gridOffsetX + decor.col * ts + ts / 2;
      const y = this.gridOffsetY + decor.row * ts + ts / 2;
      const texKey = DECOR_TEXTURES[decor.type];
      const img = this.add.image(x, y, texKey);
      img.setDisplaySize(ts - 2, ts - 2);
      img.setDepth(3);
      this.gridContainer.add(img);
    }
  }

  private drawPathsAndUpdateList(): void {
    PathEd.drawPaths(this.pathGfx, this.getPathEditorState());
    PathEd.updatePathListText(this.pathListText, this.paths);
  }

  // ══════════════════════════════════════════
  //  INPUT HANDLING
  // ══════════════════════════════════════════

  private handleGridClick(px: number, py: number): void {
    const col = Math.floor((px - this.gridOffsetX) / this.tileSize);
    const row = Math.floor((py - this.gridOffsetY) / this.tileSize);
    if (row < 0 || row >= this.gridRows || col < 0 || col >= this.gridCols) return;

    if (this.mode === EditorMode.PAINT) {
      if (this.grid[row][col] !== this.selectedTile) {
        this.grid[row][col] = this.selectedTile;
        this.decorations = this.decorations.filter(d => !(d.col === col && d.row === row));
        this.renderGrid();
      }
    } else if (this.mode === EditorMode.PATH) {
      if (!this.isPainting) return;
      this.isPainting = false;
      this.pushUndo();
      PathEd.addWaypoint(this.getPathEditorState(), row, col, (t) => this.setStatus(t));
      this.drawPathsAndUpdateList();
    } else if (this.mode === EditorMode.ERASE) {
      let changed = false;
      if (this.grid[row][col] !== TileType.GROUND) {
        this.grid[row][col] = TileType.GROUND;
        changed = true;
      }
      const decorIdx = this.decorations.findIndex(d => d.col === col && d.row === row);
      if (decorIdx >= 0) {
        this.decorations.splice(decorIdx, 1);
        changed = true;
      }
      if (changed) this.renderGrid();
    } else if (this.mode === EditorMode.DECOR || this.mode === EditorMode.RESOURCE) {
      const tile = this.grid[row][col];
      if (tile === TileType.WALL || tile === TileType.PATH || tile === TileType.BASE || tile === TileType.SPAWN) {
        return;
      }
      if (this.mode === EditorMode.RESOURCE && (tile === TileType.PLACEMENT || tile === TileType.BROKEN_PLACEMENT)) {
        return;
      }
      const existing = this.decorations.findIndex(d => d.col === col && d.row === row);
      if (existing >= 0 && this.decorations[existing].type === this.selectedDecor) {
        if (!this.isPainting) {
          this.decorations.splice(existing, 1);
          this.renderGrid();
        }
      } else {
        if (existing >= 0) this.decorations.splice(existing, 1);
        this.decorations.push({ type: this.selectedDecor, col, row });
        this.renderGrid();
      }
    }
  }

  // ══════════════════════════════════════════
  //  PATH EDITING (delegates to PathEditor)
  // ══════════════════════════════════════════

  private getPathEditorState(): PathEd.PathEditorState {
    return {
      paths: this.paths,
      currentPath: this.currentPath,
      editingPathIndex: this.editingPathIndex,
      grid: this.grid,
      gridCols: this.gridCols,
      gridRows: this.gridRows,
      tileSize: this.tileSize,
      gridOffsetX: this.gridOffsetX,
      gridOffsetY: this.gridOffsetY,
    };
  }

  /** Sync back mutable state from PathEditor functions that reassign arrays. */
  private syncPathState(state: PathEd.PathEditorState): void {
    this.paths = state.paths;
    this.currentPath = state.currentPath;
    this.editingPathIndex = state.editingPathIndex;
  }

  private finishCurrentPath(): void {
    const state = this.getPathEditorState();
    PathEd.finishCurrentPath(state, (t) => this.setStatus(t));
    this.syncPathState(state);
    this.drawPathsAndUpdateList();
  }

  private deleteLastPath(): void {
    const state = this.getPathEditorState();
    PathEd.deleteLastPath(state, () => this.pushUndo(), (t) => this.setStatus(t));
    this.syncPathState(state);
    this.drawPathsAndUpdateList();
  }

  private autoGeneratePaths(): void {
    const state = this.getPathEditorState();
    PathEd.autoGeneratePaths(state, () => this.pushUndo(), (t) => this.setStatus(t));
    this.syncPathState(state);
    this.drawPathsAndUpdateList();
  }

  // ══════════════════════════════════════════
  //  TOOLBAR (delegates to EditorToolbar)
  // ══════════════════════════════════════════

  private buildToolbarUI(): void {
    const self = this;
    const refs: ToolbarRefs = buildToolbar({
      scene: this,
      selectedTile: this.selectedTile,
      selectedDecor: this.selectedDecor,
      mode: this.mode,
      campaignLevelId: this.campaignLevelId,
      returnTo: this.returnTo,
      get gridCols() { return self.gridCols; },
      set gridCols(v: number) { self.gridCols = v; },
      get gridRows() { return self.gridRows; },
      set gridRows(v: number) { self.gridRows = v; },
      selectTile: (type) => this.selectTile(type),
      setMode: (mode) => this.setMode(mode),
      resizeGrid: (cols, rows) => this.resizeGrid(cols, rows),
      undo: () => this.undo(),
      redo: () => this.redo(),
      renderGrid: () => this.renderGrid(),
      updateSizeText: () => this.updateSizeText(),
    });

    this.toolBtns = refs.toolBtns;
    this.modeBtns = refs.modeBtns;
    this.decorBtns = refs.decorBtns;
    this.resourceBtns = refs.resourceBtns;
    this.statusText = refs.statusText;
    this.sizeText = refs.sizeText;
    this.undoText = refs.undoText;
    this.pathListText = refs.pathListText;
    this.paintSection = refs.paintSection;
    this.pathSection = refs.pathSection;
    this.decorSection = refs.decorSection;
    this.resourceSection = refs.resourceSection;
    this.eraseSection = refs.eraseSection;
    this.paletteVisibleH = refs.paletteVisibleH;

    this.updateModeButtons();
  }

  selectTile(type: TileType): void {
    this.selectedTile = type;
    for (const [t, rect] of this.toolBtns) {
      rect.setStrokeStyle(2, t === type ? 0xffcc00 : 0x333355);
    }
  }

  setMode(mode: EditorMode): void {
    if (this.mode === EditorMode.PATH && mode !== EditorMode.PATH && this.currentPath.length > 0) {
      this.finishCurrentPath();
    }
    this.mode = mode;

    this.paintSection.setVisible(mode === EditorMode.PAINT);
    this.pathSection.setVisible(mode === EditorMode.PATH);
    this.decorSection.setVisible(mode === EditorMode.DECOR);
    this.resourceSection.setVisible(mode === EditorMode.RESOURCE);
    this.eraseSection.setVisible(mode === EditorMode.ERASE);
    this.paletteScrollY = 0;
    this.getActiveSection()?.setY(0);

    this.updateModeButtons();
    const statusMessages: Record<EditorMode, string> = {
      [EditorMode.PAINT]: 'Paint mode. Click/drag tiles to paint.',
      [EditorMode.PATH]: 'Path mode. Click tiles to add waypoints. ESC to finish.',
      [EditorMode.DECOR]: 'Decor mode. Click/drag to place decorations.',
      [EditorMode.RESOURCE]: 'Resource mode. Place harvestable objects.',
      [EditorMode.ERASE]: 'Eraser mode. Click/drag to erase tiles and decorations.',
    };
    this.setStatus(statusMessages[mode]);
  }

  private updateModeButtons(): void {
    updateModeButtons(this.modeBtns, this.mode);
  }

  setStatus(text: string): void {
    this.statusText.setText(text);
  }

  private getActiveSection(): Phaser.GameObjects.Container | null {
    switch (this.mode) {
      case EditorMode.PAINT: return this.paintSection;
      case EditorMode.DECOR: return this.decorSection;
      case EditorMode.RESOURCE: return this.resourceSection;
      default: return null;
    }
  }

  private scrollPalette(dy: number): void {
    const section = this.getActiveSection();
    if (!section) return;

    const bounds = section.getBounds();
    const contentH = bounds.height;
    const maxScroll = Math.max(0, contentH - this.paletteVisibleH);

    this.paletteScrollY = Phaser.Math.Clamp(
      this.paletteScrollY + dy * 0.5,
      0,
      maxScroll,
    );
    section.setY(-this.paletteScrollY);
  }

  updateSizeText(): void {
    this.sizeText.setText(`${this.gridCols} × ${this.gridRows}`);
  }

  // ══════════════════════════════════════════
  //  PROPERTIES PANEL (right side)
  // ══════════════════════════════════════════

  private buildPropsPanel(): void {
    this.propsPanel = new PropsPanel(this, {
      promptName: () => { const s = this.getMapIOState(); MapIO.promptName(this, s, () => this.syncFromState(s)); },
      cycleDifficulty: (dir) => { const s = this.getMapIOState(); MapIO.cycleDifficulty(s, dir, DIFFICULTIES); this.syncFromState(s); },
      adjustWaveCount: (delta) => { const s = this.getMapIOState(); MapIO.adjustWaveCount(s, delta); this.syncFromState(s); },
      toggleEnemy: (type, cb) => MapIO.toggleEnemy(this.getMapIOState(), type, cb),
      toggleGrade: (grade, cb) => MapIO.toggleGrade(this.getMapIOState(), grade, cb),
      exportMap: () => MapIO.exportMap(this.getMapIOState(), (t) => this.setStatus(t)),
      exportClipboard: () => MapIO.exportClipboard(this.getMapIOState(), (t) => this.setStatus(t)),
      importMap: () => { const s = this.getMapIOState(); MapIO.importMap(s, () => this.pushUndo(), () => this.updateSizeText(), () => this.renderGrid(), (t) => this.setStatus(t)); this.syncFromState(s); },
      importClipboard: () => { const s = this.getMapIOState(); MapIO.importClipboard(s, () => this.pushUndo(), () => this.updateSizeText(), () => this.renderGrid(), (t) => this.setStatus(t)); this.syncFromState(s); },
      clearAll: () => {
        this.pushUndo();
        this.initGrid();
        this.paths = [];
        this.currentPath = [];
        this.decorations = [];
        this.renderGrid();
        this.setStatus('Grid cleared.');
      },
      cycleComplexity: (dir) => { const s = this.getMapIOState(); MapIO.cycleComplexity(s, dir); this.syncFromState(s); },
      cycleRichness: (dir) => { const s = this.getMapIOState(); MapIO.cycleRichness(s, dir); this.syncFromState(s); },
      cycleHazards: (dir) => { const s = this.getMapIOState(); MapIO.cycleHazards(s, dir); this.syncFromState(s); },
      cycleIntensity: (dir) => { const s = this.getMapIOState(); MapIO.cycleIntensity(s, dir); this.syncFromState(s); },
      generateProceduralMap: () => { const s = this.getMapIOState(); MapIO.generateProceduralMap(s, () => this.pushUndo(), () => this.updateSizeText(), () => this.renderGrid(), (t) => this.setStatus(t)); this.syncFromState(s); },
      saveToCampaign: () => MapIO.saveToCampaign(this.getMapIOState(), (t) => this.setStatus(t)),
      resetToFactoryMap: () => { const s = this.getMapIOState(); MapIO.resetToFactoryMap(this, s); this.syncFromState(s); },
      saveToMapList: () => { const s = this.getMapIOState(); MapIO.saveToMapList(s, (t) => this.setStatus(t)); this.syncFromState(s); },
      setStatus: (text) => this.setStatus(text),
      pickTile: (cb) => { this.tilePickCallback = cb; },
      getMapWaveCount: () => this.mapWaveCount,
      getGridSize: () => ({ cols: this.gridCols, rows: this.gridRows }),
    }, {
      propsX: PROPS_X,
      propsW: PROPS_W,
      canvasH: CANVAS_HEIGHT,
      campaignLevelId: this.campaignLevelId,
      mapName: this.mapName,
      mapDifficulty: this.mapDifficulty,
      mapWaveCount: this.mapWaveCount,
      complexityLabel: COMPLEXITY_LABELS[this.complexity],
      richnessLabel: RICHNESS_LABELS[this.richness],
      hazardsLabel: HAZARD_LABELS[this.hazards],
      intensityLabel: INTENSITY_LABELS[this.intensity],
      allowedEnemies: this.allowedEnemies,
      allowedGrades: this.allowedGrades,
      allEnemyTypes: ALL_ENEMY_TYPES,
      cinematicTriggers: this.cinematicTriggers,
    });
    this.propsPanel.build();
  }

  /** Build the MapIO state object that references live scene state. */
  private getMapIOState(): MapIO.MapIOState {
    return {
      grid: this.grid,
      gridCols: this.gridCols,
      gridRows: this.gridRows,
      paths: this.paths,
      currentPath: this.currentPath,
      mapName: this.mapName,
      mapId: this.mapId,
      mapDifficulty: this.mapDifficulty,
      mapWaveCount: this.mapWaveCount,
      allowedEnemies: this.allowedEnemies,
      allowedGrades: this.allowedGrades,
      decorations: this.decorations,
      cinematicTriggers: this.cinematicTriggers,
      complexity: this.complexity,
      richness: this.richness,
      hazards: this.hazards,
      intensity: this.intensity,
      campaignLevelId: this.campaignLevelId,
      hasOverride: this.hasOverride,
      propsPanel: this.propsPanel,
    };
  }

  /** Copy primitive fields back from a MapIOState snapshot into live scene state. */
  private syncFromState(s: MapIO.MapIOState): void {
    this.grid = s.grid;
    this.gridCols = s.gridCols;
    this.gridRows = s.gridRows;
    this.paths = s.paths;
    this.currentPath = s.currentPath;
    this.mapName = s.mapName;
    this.mapId = s.mapId;
    this.mapDifficulty = s.mapDifficulty;
    this.mapWaveCount = s.mapWaveCount;
    this.complexity = s.complexity;
    this.richness = s.richness;
    this.hazards = s.hazards;
    this.intensity = s.intensity;
    this.decorations = s.decorations;
    this.allowedEnemies = s.allowedEnemies;
    this.allowedGrades = s.allowedGrades;
  }

  // ══════════════════════════════════════════
  //  TOP BAR
  // ══════════════════════════════════════════

  private buildTopBar(): void {
    const barX = GRID_AREA_X;
    const barW = GRID_AREA_W;
    this.add.rectangle(barX + barW / 2, 18, barW, 36, 0x0d0d1a, 0.9).setDepth(10);
    this.add.text(barX + barW / 2, 18, 'MAP EDITOR', {
      fontSize: '16px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    this.zoomText = this.add.text(GRID_AREA_X + GRID_AREA_W, 28, 'x1.0', {
      fontSize: '10px', color: '#66ccaa', fontFamily: 'monospace',
    }).setOrigin(1, 0.5).setDepth(11);
    this.updateZoomText();

    this.fpsText = this.add.text(GRID_AREA_X, 18, '60 FPS', {
      fontSize: '10px', color: '#88ff88', fontFamily: 'monospace',
    }).setOrigin(0, 0.5).setDepth(11);
  }

  private updateZoomText(): void {
    if (this.zoomText) {
      this.zoomText.setText(`x${this.zoomLevel.toFixed(1)}  [scroll=zoom  mid/right-drag=pan  0=reset]`);
    }
  }
}
